import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/** One page of `queryField`, sorted on the server by `order`. Shared by My and Manage Attendance. */
export const buildListQuery = (
  alias: string,
  queryField: string,
  itemFields: string,
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = []
) => `
  query {
    ${alias}: ${queryField}(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items { ${itemFields} }
    }
  }
`;

const ATTENDANCE_FIELDS = `
  ItemId
  CreatedDate
  LastUpdatedDate
  AttendanceDate
  StartTimeUtc
  EndTimeUtc
  Timezone
  UserId
  DisplayName
  DepartmentId
  DepartmentName
  DepartmentKey
  OverTimeInSecond
  TotalWorkTimeInSecond
  TotalBreakTimeInSecond
  ExpectedWorkingHourInSecond
  TotalClockTimeInSecond
  IsOpen
  IsForceClockOut
  Status
  BreakTimeDetails {
    StartTimeUtc
    EndTimeUtc
    Type
  }
`;

/** Newest day first by default. Shared with Manage Attendance. */
export const DEFAULT_ATTENDANCES_ORDER: OrderBy[] = [
  { field: 'AttendanceDate', direction: 'DESC' },
];

export const buildGetAttendancesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_ATTENDANCES_ORDER
) =>
  buildListQuery(
    'Attendances',
    'getAttendances',
    ATTENDANCE_FIELDS,
    where,
    pageNo,
    pageSize,
    order
  );

const ATTENDANCE_CORRECTION_FIELDS = `
  ItemId
  CreatedDate
  AttendanceId
  UserId
  DepartmentId
  DepartmentName
  DepartmentKey
  CorrectionDate
  StartTimeUtc
  EndTimeUtc
  Timezone
  BreakTimeDetails {
    StartTimeUtc
    EndTimeUtc
    Type
  }
  Comment
  Status
  Note
  Fine
`;

/** By default latest corrected day first, like the legacy `GetUserAttendanceCorrection` sort
 * `{ CorrectionDateUTC: -1 }` (AttendanceCorrectionService.cs:132); ties go to the latest change, as in
 * KosovoAttendanceCorrectionService.cs:363. Shared with Manage Attendance. */
export const DEFAULT_ATTENDANCE_CORRECTIONS_ORDER: OrderBy[] = [
  { field: 'CorrectionDate', direction: 'DESC' },
  { field: 'LastUpdatedDate', direction: 'DESC' },
];

export const buildGetAttendanceCorrectionsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_ATTENDANCE_CORRECTIONS_ORDER
) =>
  buildListQuery(
    'AttendanceCorrections',
    'getAttendanceCorrections',
    ATTENDANCE_CORRECTION_FIELDS,
    where,
    pageNo,
    pageSize,
    order
  );

/**
 * The employee's current HR term — only its date range is needed to scope the fine totals below.
 *
 * Known gap: `UserHRPeriodicalProfile.UserId` is populated with the `UserProfileDetail` document's
 * own id by `createEmployee` in hr-directory for terms created before that was fixed, not the IAM
 * user id — filtering by the auth user id here will find no row for those older terms.
 */
export const buildGetMyHrPeriodQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) =>
  buildListQuery(
    'HrProfiles',
    'getUserHRPeriodicalProfiles',
    'HRPeriodStartDate HRPeriodEndDate',
    where,
    pageNo,
    pageSize
  );

/**
 * The employee's HR terms with their work days and hours, for the rules that read the day's scheduled
 * hours (the correction minimum). Every term is read and matched by date with `hrTermOn`. Same known
 * gap on `UserId` as `buildGetMyHrPeriodQuery`.
 */
export const buildGetMyHrTermsQuery = (where: Record<string, unknown>) =>
  buildListQuery(
    'HrProfiles',
    'getUserHRPeriodicalProfiles',
    'HRPeriodStartDate HRPeriodEndDate IsCurrentHrYear WorkDays { WorkDay WorkingHours EffectiveFrom }',
    where,
    1,
    50
  );

/** An employee's approved and pending leave overlapping the day list's range: what a day on leave
 * shows. */
export const buildGetDayListLeavesQuery = (where: Record<string, unknown>, pageSize: number) =>
  buildListQuery(
    'LeaveRequests',
    'getLeaveRequests',
    'Status Title LeaveCategory LeaveType StartDate EndDate',
    where,
    1,
    pageSize
  );

/** The organization's holidays in the day list's range. */
export const buildGetDayListHolidaysQuery = (where: Record<string, unknown>, pageSize: number) =>
  buildListQuery('Holidays', 'getHolidays', 'Name StartDate', where, 1, pageSize);

/**
 * Lunch/non-compliance fine records charged to the employee (see `getMyFineSummary`).
 *
 * Known gap: `Fine.UserId` is populated with the `UserProfileDetail` document's own id by
 * whatever backend process creates Fine records, not the IAM user id — filtering by the auth
 * user id here will find no rows until that process is updated too.
 */
export const buildGetMyFinesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = []
) =>
  buildListQuery(
    'Fines',
    'getFines',
    'FineType FineAmount FineCreatedDateUtc',
    where,
    pageNo,
    pageSize,
    order
  );
